/**
 * Where a new invoice's work-detail window starts, and which earlier invoices it would re-report.
 *
 * Work-detail rows are re-materialised from time registrations on every invoice and nothing is ever
 * marked as invoiced — deliberately, because that is what keeps deleting a draft invoice cheap and
 * correct. The cost of that design is that the same hours can be reported on any number of invoices
 * for the same project. The window is the only guard, so this module makes the window default to
 * where the last one left off, and makes an overlap visible when the operator widens it again.
 *
 * Pure and Relay-free so it can be tested directly, and shared by the Step 1 date control and the
 * Step 2 review panel without either becoming the source of truth for the other.
 *
 * Dates are ISO yyyy-MM-dd strings throughout. String order on ISO dates is date order, so
 * comparison needs no date library and no timezone.
 */

/** A work-detail window already reported on one of a project's invoices. */
export type InvoicedWorkDetailPeriod = {
	invoiceId?: string | null;
	companyInvoiceId?: number | null;
	invoiceName?: string | null;
	startDate?: string | null;
	endDate?: string | null;
};

/** The subset of a project this module reads. */
export type WorkDetailPeriodProject = {
	id?: string | null;
	invoicedWorkDetailPeriods?: ReadonlyArray<InvoicedWorkDetailPeriod> | null;
};

/** One earlier invoice whose work-detail window overlaps the window being chosen. */
export type WorkDetailOverlap = {
	invoiceId: string | null;
	companyInvoiceId: number | null;
	invoiceName: string | null;
	startDate: string;
	endDate: string;
};

/** Only a period with both ends set can bound anything; a half-open one is unusable either way. */
const isComplete = (period: InvoicedWorkDetailPeriod): boolean => !!period.startDate && !!period.endDate;

const completePeriodsOf = (project: WorkDetailPeriodProject): InvoicedWorkDetailPeriod[] =>
	(project?.invoicedWorkDetailPeriods ?? []).filter(isComplete);

/** ISO date `days` after `isoDate`, computed in UTC so a local timezone cannot shift the day. */
export const addDays = (isoDate: string, days: number): string => {
	const [year, month, day] = isoDate.split('-').map(Number);
	return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
};

/**
 * The latest date any of this project's invoices has already reported work up to, or null when it
 * has never carried work detail.
 */
export const latestInvoicedWorkDetailEnd = (project: WorkDetailPeriodProject): string | null => {
	const ends = completePeriodsOf(project).map(period => period.endDate as string);
	return ends.length ? ends.reduce((latest, end) => (end > latest ? end : latest)) : null;
};

/**
 * Where the work-detail window should start for the selected projects.
 *
 * A project that has already had work reported contributes the day after its latest reported end.
 * A project that has not contributes `inceptionDate`, the caller's "everything to date" lower bound.
 * The EARLIEST of those candidates wins, so a project newly added to the selection is never silently
 * under-reported. That direction is chosen on purpose: reporting too much now raises a visible
 * warning (see {@link overlappingWorkDetailInvoices}), while reporting too little would raise
 * nothing at all.
 *
 * With no selected project carrying prior work detail this returns `inceptionDate` unchanged, which
 * is the right default for a first invoice.
 *
 * Clamped to `endDate`: once everything up to the end of the window has already been reported, the
 * next start would fall after it, and an inverted range is a broken control rather than an empty
 * one. The resulting one-day overlap is then reported by the warning, which is the honest outcome —
 * there is genuinely nothing new to show.
 */
export const nextWorkDetailWindowStart = (
	projects: ReadonlyArray<WorkDetailPeriodProject>,
	inceptionDate: string,
	endDate: string
): string => {
	const candidates = (projects ?? []).map(project => {
		const latestEnd = latestInvoicedWorkDetailEnd(project);
		return latestEnd ? addDays(latestEnd, 1) : inceptionDate;
	});
	const earliest = candidates.length ? candidates.reduce((min, date) => (date < min ? date : min)) : inceptionDate;
	return earliest > endDate ? endDate : earliest;
};

/**
 * The earlier invoices whose work-detail window overlaps `[startDate, endDate]` on any of these
 * projects, earliest window first.
 *
 * Deduplicated by invoice: one invoice billing several selected projects reports the same window
 * once per project, and naming it twice in a warning would read as two separate invoices.
 *
 * Overlap is reported, never blocked. Re-issuing a corrected invoice legitimately covers the same
 * period, so the operator has to be able to proceed.
 *
 * Only ever asked about an invoice that does not exist yet, so nothing here has to exclude an
 * invoice from overlapping itself. A surface that runs this while EDITING a saved invoice would
 * need to.
 */
export const overlappingWorkDetailInvoices = (
	projects: ReadonlyArray<WorkDetailPeriodProject>,
	startDate: string | null | undefined,
	endDate: string | null | undefined
): WorkDetailOverlap[] => {
	if (!startDate || !endDate) return [];

	const byInvoice = new Map<string, WorkDetailOverlap>();
	(projects ?? []).forEach(project => {
		completePeriodsOf(project).forEach(period => {
			const periodStart = period.startDate as string;
			const periodEnd = period.endDate as string;
			// Two closed ranges overlap unless one ends before the other begins.
			if (periodEnd < startDate || periodStart > endDate) return;
			const invoiceId = period.invoiceId ?? null;
			const key = invoiceId ?? `companyInvoiceId:${period.companyInvoiceId ?? ''}`;
			if (byInvoice.has(key)) return;
			byInvoice.set(key, {
				invoiceId,
				companyInvoiceId: period.companyInvoiceId ?? null,
				invoiceName: period.invoiceName ?? null,
				startDate: periodStart,
				endDate: periodEnd,
			});
		});
	});

	return Array.from(byInvoice.values()).sort(
		(a, b) => a.startDate.localeCompare(b.startDate) || (a.companyInvoiceId ?? 0) - (b.companyInvoiceId ?? 0)
	);
};

/**
 * How an overlapping invoice is named in the warning: the operator's own name for it where there is
 * one, otherwise its number. An invoice with neither is named by its number placeholder rather than
 * dropped, so the count in the warning always matches the list.
 */
export const workDetailOverlapLabel = (overlap: WorkDetailOverlap): string =>
	overlap.invoiceName?.trim() || `#${overlap.companyInvoiceId ?? ''}`;

/** The overlapping invoices as one comma-separated list for the warning copy. */
export const workDetailOverlapLabels = (overlaps: ReadonlyArray<WorkDetailOverlap>): string =>
	overlaps.map(workDetailOverlapLabel).join(', ');
